/**
 * UpdateManagedSqlDbModal Component
 *
 * Modal for updating managed SQL database metadata (name and description).
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useUpdateManagedSqlDb } from '../hooks/useUpdateManagedSqlDb';
import {
  updateManagedSqlDbFormSchema,
  type UpdateManagedSqlDbFormData,
} from '../../../lib/validators/managed-sql-db';
import type { ManagedSqlDbSanitized } from '../../../types/managed-sql-db.types';

interface UpdateManagedSqlDbModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  managedSqlDb: ManagedSqlDbSanitized;
}

/**
 * UpdateManagedSqlDbModal component
 */
export const UpdateManagedSqlDbModal: React.FC<UpdateManagedSqlDbModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  managedSqlDb,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateManagedSqlDbFormData>({
    resolver: zodResolver(updateManagedSqlDbFormSchema),
    defaultValues: {
      managedSqlDbName: managedSqlDb.managedSqlDbName,
      description: managedSqlDb.description || '',
    },
  });

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      reset({
        managedSqlDbName: managedSqlDb.managedSqlDbName,
        description: managedSqlDb.description || '',
      });
    }
  }, [isOpen, managedSqlDb, reset]);

  const { mutate: updateManagedSqlDb, isPending } = useUpdateManagedSqlDb(tenantId, {
    onSuccess: () => {
      setSuccessMessage('Database updated successfully!');
      setErrorMessage('');
      setTimeout(() => {
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: UpdateManagedSqlDbFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    updateManagedSqlDb({
      managedSqlDbId: managedSqlDb.managedSqlDbId,
      data: {
        managedSqlDbName: data.managedSqlDbName,
        description: data.description || undefined,
      },
    });
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      setSuccessMessage('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Edit Managed SQL Database</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Info Note */}
          <Alert variant="info">
            <span className="font-medium">Database Code:</span> {managedSqlDb.managedSqlDbCode}
            <br />
            <span className="text-xs">Code cannot be changed after creation.</span>
          </Alert>

          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Database Name */}
          <FormField label="Database Name" required error={errors.managedSqlDbName?.message}>
            <Input
              {...register('managedSqlDbName')}
              placeholder="e.g., Production Database"
              error={!!errors.managedSqlDbName}
            />
          </FormField>

          {/* Description */}
          <FormField label="Description" error={errors.description?.message}>
            <Textarea
              {...register('description')}
              placeholder="Describe the purpose of this database..."
              rows={3}
              error={!!errors.description}
            />
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
